<template>
  <div class="mfc">
    <div ref="mapEl" class="mfc-map"></div>

    <div v-if="mapError" class="mfc-erreur">
      <i class="fas fa-exclamation-triangle"></i>
      {{ mapError }}
    </div>

    <div v-if="note" class="mfc-note">
      <i class="fas fa-map-marker-alt"></i> {{ note }}
    </div>

    <div v-if="$slots.legende" class="mfc-legende">
      <slot name="legende" />
    </div>
  </div>
</template>

<script setup>
/**
 * La carte de France des formateurs — le socle, partagé par les deux écrans qui
 * en posent une.
 *
 * POURQUOI CE COMPOSANT EXISTE. La diffusion d'une mission
 * (`/missions/:id/diffusion`) dessinait déjà cette carte : chargement du script
 * Google, fond départemental en GeoJSON, dispersion des points qui se
 * superposent, bulle d'information, surlignage et sélection. La liste des
 * formateurs (`/formateurs`) veut exactement la même carte, sans mission :
 * personne ne doit la réécrire, et surtout les deux ne doivent pas diverger —
 * un même formateur posé à deux endroits différents selon l'écran serait un
 * défaut que rien ne rattraperait.
 *
 * CE QU'IL NE SAIT PAS, ET NE DOIT PAS APPRENDRE : ce qu'est une mission, une
 * compétence, un statut ou une diffusion. Il pose des points et les peint avec
 * la couleur qu'on lui donne (`couleurDe`), il ouvre la bulle qu'on lui écrit
 * (`contenuBulle`), et il rend la légende que la page lui passe en slot. Toute
 * la connaissance métier reste chez l'appelant.
 *
 * TROIS ÉTATS, ET LEUR ORDRE. Surligné (orange) l'emporte sur retenu (accent de
 * l'interface), qui l'emporte sur la couleur de base. C'est l'ordre de lecture :
 * le surlignage répond à un geste qu'on vient de faire, la sélection à un choix
 * déjà posé, la couleur de base à ce que le formateur EST.
 */
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useGoogleMapsLoader } from '../../composables/useGoogleMapsLoader.js'
import { COULEUR_RAYON, COULEUR_RAYON_ANCIEN } from './map-couleurs.js'

const props = defineProps({
  /** Chaque entrée doit porter `id` et `coords: { lat, lon }` ; sans coords, pas de point. */
  formateurs: { type: Array, default: () => [] },
  /** (formateur) → couleur du point quand il n'est ni surligné ni retenu. */
  couleurDe: { type: Function, default: () => '#6b7280' },
  /** (formateur) → le HTML de la bulle. Un élément `[data-fiche]` y devient un lien émis. */
  contenuBulle: { type: Function, required: true },
  /** (formateur) → l'infobulle native du point, celle qui sort au survol. */
  titreDe: { type: Function, default: () => '' },
  /**
   * (formateur) → qui passe devant qui, à état égal. Le socle ne connaît que
   * ses trois états (surligné, retenu, ordinaire) ; ce qui départage DEUX
   * points ordinaires — la proximité d'une mission, par exemple — appartient à
   * la page.
   */
  rangDe: { type: Function, default: () => 1 },
  highlightedId: { type: Number, default: null },
  selectedIds: { type: Set, default: () => new Set() },
  /** Point de repère à marquer (le lieu d'une mission), et son cercle de rayon. */
  centre: { type: Object, default: null },
  centreTitre: { type: String, default: '' },
  rayonKm: { type: Number, default: null },
  /** Les anciens rayons (une zone élargie) : des cercles gris sous celui du rayon actuel. */
  rayonsAvantKm: { type: Array, default: () => [] },
  /** Département peint en bleu sur le fond (celui de la mission, ou celui qu'on filtre). */
  departementSurligne: { type: String, default: null },
  /** Phrase posée en haut à gauche : précision de la position du centre. */
  note: { type: String, default: null },
})

// `bulle-ouverte` porte (formateur, racine) : la racine est l'élément de la
// bulle ouverte, seul endroit où la page doit écrire ce qu'elle y complète.
const emit = defineEmits(['highlight', 'ouvrir', 'bulle-ouverte'])

/**
 * Les formateurs indexés par identifiant. `repeindre` parcourt tous les
 * marqueurs et cherchait le formateur de chacun par un `find()` : sur la carte
 * de la liste, qui n'est pas paginée, mille points font un million de
 * comparaisons À CHAQUE survol.
 */
const parId = computed(() => new Map(props.formateurs.map((f) => [f.id, f])))

const MARGE_CADRAGE_PX = 12

const mapEl = ref(null)
const mapError = ref(null)
const { loadGoogleMapsScript } = useGoogleMapsLoader()

let map = null
let markers = []
let centreMarker = null
let cercleRayon = null
let cerclesAvant = []
let centreLatLng = null
let infoWindow = null
// Le formateur dont la bulle est ouverte, et l'élément qui la porte : `domready`
// ne dit ni de qui il s'agit, ni où chercher.
let formateurBulle = null
let racineBulle = null

/**
 * Referme la bulle et oublie son formateur : plus rien à compléter après coup.
 * Oublié AVANT de fermer : la fermeture voulue par le code ne passe pas pour
 * un geste du visiteur (`surBulleFermee`).
 */
function fermerBulle() {
  formateurBulle = null
  racineBulle = null
  if (infoWindow) infoWindow.close()
}
let ecouteBulle = null
let ecouteFermeture = null
let geojsonLoaded = false
const jitterCache = new Map()

const HIGHLIGHT_COLOR = '#f97316'

/**
 * Couleur d'un formateur DÉJÀ RETENU : l'accent de l'interface, LU au montage
 * et non écrit en dur (C41). L'API de Google veut une chaîne et non une
 * `var()`, mais un littéral ferait fuir l'accent d'une version de l'interface
 * dans l'autre et casserait le thème vert de staging. Le repli est NEUTRE — un
 * gris que personne ne confondra avec un accent — plutôt que la valeur de
 * l'une des deux palettes.
 */
const SELECTED_COLOR_FALLBACK = '#64748b'
const selectedColor = ref(SELECTED_COLOR_FALLBACK)

function lireAccent() {
  const valeur = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim()
  if (valeur) selectedColor.value = valeur
}

const COMMUNE_JITTER_RANGE = 0.004 // ≈ 200 m — dé-empile les formateurs d'une même commune
const DEPT_JITTER_RANGE = 0.15 // ≈ 8 km — position connue seulement au département

/**
 * Dispersion appliquée aux points pour qu'ils ne se superposent pas quand
 * plusieurs formateurs partagent la même position. Son amplitude suit la
 * précision : quelques centaines de mètres autour d'une commune, une dispersion
 * large quand on n'a que le centroïde du département.
 *
 * Désactivée quand un rayon ≤ 5 km est actif : le filtre se base sur la distance
 * calculée côté serveur SANS dispersion, un point dispersé pourrait sinon
 * apparaître hors du cercle tracé.
 */
function getJitter(formateurId, precision) {
  if (props.rayonKm != null && props.rayonKm <= 5) return { lat: 0, lng: 0 }
  const range = precision === 'commune' ? COMMUNE_JITTER_RANGE : DEPT_JITTER_RANGE
  const key = `${formateurId}:${range}`
  if (!jitterCache.has(key)) {
    jitterCache.set(key, {
      lat: (Math.random() - 0.5) * range,
      lng: (Math.random() - 0.5) * range,
    })
  }
  return jitterCache.get(key)
}

function centreIcon() {
  return {
    path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
    fillColor: COULEUR_RAYON,
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#ffffff',
    scale: 7,
  }
}

function formateurIcon(color, isHighlighted, isSelected = false) {
  return {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: color,
    fillOpacity: isHighlighted || isSelected ? 1 : 0.85,
    strokeWeight: isHighlighted ? 3 : isSelected ? 2.5 : 1.5,
    strokeColor: isHighlighted ? HIGHLIGHT_COLOR : '#ffffff',
    // Un formateur retenu est plus gros que les autres : c'est ce qui permet de
    // lire la sélection sans compter les points.
    scale: isHighlighted ? 9 : isSelected ? 8 : 6,
  }
}

function couleurMarqueur(formateur, isHighlighted, isSelected) {
  if (isHighlighted) return HIGHLIGHT_COLOR
  if (isSelected) return selectedColor.value
  return props.couleurDe(formateur) || '#6b7280'
}

function zIndexMarqueur(isHighlighted, isSelected, formateur) {
  if (isHighlighted) return 999
  if (isSelected) return 500
  return props.rangDe(formateur) || 1
}

onMounted(async () => {
  lireAccent()
  try {
    await loadGoogleMapsScript()
    initMap()
  } catch (err) {
    mapError.value = err.message || 'Erreur de chargement de la carte'
  }
})

onBeforeUnmount(() => {
  clearMarkers()
  if (cercleRayon) cercleRayon.setMap(null)
  cerclesAvant.forEach((c) => c.setMap(null))
  if (ecouteBulle) ecouteBulle.remove()
  if (ecouteFermeture) ecouteFermeture.remove()
  fermerBulle()
})

function initMap() {
  if (!mapEl.value || !window.google) return

  map = new google.maps.Map(mapEl.value, {
    center: { lat: 46.6, lng: 1.9 },
    zoom: 6,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
    styles: [
      { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#aaaaaa' }] },
      { featureType: 'water', stylers: [{ color: '#d4e6f1' }] },
    ],
  })

  // La bulle se borne elle-même : sans `maxWidth`, Google la dimensionne sur le
  // contenu mesuré avant nos styles, et la colonne de droite se retrouvait
  // rognée. 320 laisse la place aux 280 du gabarit et à ses marges.
  infoWindow = new google.maps.InfoWindow({ maxWidth: 320 })

  ecouteBulle = google.maps.event.addListener(infoWindow, 'domready', surBulleOuverte)
  ecouteFermeture = google.maps.event.addListener(infoWindow, 'close', surBulleFermee)

  loadGeoJson()
  placerCentre()
  updateMarkers()
}

/**
 * La bulle est dans le DOM : la page peut y compléter ce qui se calcule après
 * coup (le trajet routier, sur la carte des formateurs). Elle reçoit la racine
 * de CETTE bulle, pour n'écrire que là — pas dans la première `.gm-style-iw`
 * venue du document.
 */
function surBulleOuverte() {
  if (formateurBulle) emit('bulle-ouverte', formateurBulle, racineBulle)
  brancherLienFiche()
}

/**
 * Le point cliqué n'est orange que le temps de sa bulle : la refermer (croix,
 * touche Échap) lui rend sa couleur (vert, orange « en cours », ou l'accent
 * d'un retenu). Une bulle encore ouverte vient d'être reposée sur un autre
 * point : rien n'a été refermé.
 */
function surBulleFermee() {
  const ouverte = typeof infoWindow.isOpen === 'boolean' ? infoWindow.isOpen : Boolean(infoWindow.getMap?.())
  if (!formateurBulle || ouverte) return
  const avaitLaMain = formateurBulle.id === props.highlightedId
  formateurBulle = null
  racineBulle = null
  if (avaitLaMain) emit('highlight', null)
}

/**
 * La bulle est du HTML brut, hors de Vue : un lien vers la fiche n'y passerait
 * pas par le routeur et rechargerait toute l'application. On écoute donc le
 * clic sur l'élément qui porte `data-fiche` et on laisse la page router
 * elle-même. `data-branche` évite d'y poser deux écouteurs si `domready`
 * revient sur la même bulle.
 */
function brancherLienFiche() {
  const cible = racineBulle?.querySelector('[data-fiche]')
  if (!cible || cible.dataset.branche === '1') return
  cible.dataset.branche = '1'
  cible.addEventListener('click', (e) => {
    // ON NE RETIENT PAS UN LIEN QUI VISE AILLEURS. Une ancre `target="_blank"`
    // (la carte des formateurs, qui ouvre la fiche à côté) est déjà servie par
    // le navigateur : la retenir ici ouvrait DEUX fois — l'onglet, et la page
    // sous la carte. Même chose pour un ctrl/⌘-clic ou un clic molette.
    if (cible.target === '_blank' || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button === 1) return
    e.preventDefault()
    emit('ouvrir', Number(cible.dataset.fiche))
  })
}

async function loadGeoJson() {
  if (!map || geojsonLoaded) return
  try {
    const response = await fetch('/data/departements-simplified.geojson')
    const geojson = await response.json()
    map.data.addGeoJson(geojson)
    appliquerStyleDepartements()
    geojsonLoaded = true
  } catch {
    // Le fond départemental est un confort : son absence ne casse pas la carte.
  }
}

function appliquerStyleDepartements() {
  if (!map) return
  const surligne = props.departementSurligne
  map.data.setStyle((feature) => {
    const estSurligne = surligne != null && feature.getProperty('code') === surligne
    return {
      fillColor: estSurligne ? '#3b82f6' : '#f3f4f6',
      fillOpacity: estSurligne ? 0.2 : 0.05,
      strokeColor: estSurligne ? '#3b82f6' : '#d1d5db',
      strokeWeight: estSurligne ? 2 : 0.5,
    }
  })
}

function placerCentre() {
  if (!map || !props.centre) return
  const position = { lat: props.centre.lat, lng: props.centre.lon }
  centreMarker = new google.maps.Marker({
    position,
    map,
    icon: centreIcon(),
    title: props.centreTitre || undefined,
    zIndex: 1000,
  })
  centreLatLng = position
  updateCercleRayon()
}

function updateCercleRayon() {
  if (!map || !centreLatLng) return

  if (props.rayonKm == null) {
    if (cercleRayon) {
      cercleRayon.setMap(null)
      cercleRayon = null
    }
    return
  }

  if (cercleRayon) {
    cercleRayon.setCenter(centreLatLng)
    cercleRayon.setRadius(props.rayonKm * 1000)
  } else {
    cercleRayon = new google.maps.Circle({
      map,
      center: centreLatLng,
      radius: props.rayonKm * 1000,
      strokeColor: COULEUR_RAYON,
      strokeWeight: 1.5,
      fillColor: COULEUR_RAYON,
      fillOpacity: 0.08,
      clickable: false,
      zIndex: 1,
    })
  }

  updateCercleAvant()

  // UNE MARGE COURTE : le zoom est entier, et la marge par défaut de Google
  // suffisait à faire retomber d'un cran — le cercle n'occupait plus que 40 %
  // de la hauteur, la carte montrait la moitié de la France autour.
  map.fitBounds(cercleRayon.getBounds(), MARGE_CADRAGE_PX)
}

/** Les anciens rayons, en gris sous le rayon actuel. */
function updateCercleAvant() {
  cerclesAvant.forEach((c) => c.setMap(null))
  cerclesAvant = []
  if (!map || !centreLatLng) return
  cerclesAvant = props.rayonsAvantKm.map((km) => new google.maps.Circle({
    map,
    center: centreLatLng,
    radius: km * 1000,
    strokeColor: COULEUR_RAYON_ANCIEN,
    strokeOpacity: 0.9,
    strokeWeight: 2,
    fillColor: COULEUR_RAYON_ANCIEN,
    fillOpacity: 0.06,
    clickable: false,
    zIndex: 0,
  }))
}

function updateMarkers() {
  // La bulle ouverte appartient à un marqueur qu'on s'apprête à retirer : la
  // laisser, c'est laisser à l'écran une fiche qui ne correspond plus aux
  // points affichés — et, sur la carte des formateurs, un « Calcul du
  // trajet… » qui ne se remplira jamais. Le surlignage qu'elle portait part
  // avec elle — pas celui posé depuis la liste, qui ne vient pas d'une bulle.
  const surligneParLaBulle = formateurBulle && formateurBulle.id === props.highlightedId
  fermerBulle()
  if (surligneParLaBulle) emit('highlight', null)
  clearMarkers()
  if (!map) return

  for (const f of props.formateurs) {
    if (f.coords) markers.push({ marker: creerMarqueur(f), formateurId: f.id })
  }
}

/** Le point d'un formateur, dispersé et peint selon son état, et qui ouvre sa bulle au clic. */
function creerMarqueur(f) {
  const { lat: jitterLat, lng: jitterLng } = getJitter(f.id, f.coordsPrecision)
  const isHighlighted = f.id === props.highlightedId
  const isSelected = props.selectedIds.has(f.id)

  const marker = new google.maps.Marker({
    position: { lat: f.coords.lat + jitterLat, lng: f.coords.lon + jitterLng },
    map,
    icon: formateurIcon(couleurMarqueur(f, isHighlighted, isSelected), isHighlighted, isSelected),
    title: props.titreDe(f) || undefined,
    // Les retenus passent devant les autres : ce sont eux qu'on relit.
    zIndex: zIndexMarqueur(isHighlighted, isSelected, f),
  })

  marker.addListener('click', () => ouvrirBulle(f, marker))
  return marker
}

/**
 * Ouvre la bulle d'un formateur. Son HTML est posé dans un élément à nous
 * plutôt que passé en chaîne : Google l'aurait enveloppé de la même façon,
 * mais on garde ainsi la main sur la racine — `domready` la transmet à la page
 * et y cherche le lien vers la fiche, sans fouiller tout le document.
 */
function ouvrirBulle(f, marker) {
  formateurBulle = f
  racineBulle = document.createElement('div')
  racineBulle.innerHTML = props.contenuBulle(f)
  infoWindow.setContent(racineBulle)
  infoWindow.open(map, marker)
  emit('highlight', f.id)
}

/**
 * Repeindre SANS reconstruire : recréer les marqueurs fermerait la bulle
 * ouverte de celui qu'on vient justement de retenir.
 */
function repeindre() {
  if (!map) return
  for (const { marker, formateurId } of markers) {
    const isHighlighted = formateurId === props.highlightedId
    const isSelected = props.selectedIds.has(formateurId)
    const f = parId.value.get(formateurId)
    marker.setIcon(formateurIcon(couleurMarqueur(f, isHighlighted, isSelected), isHighlighted, isSelected))
    marker.setZIndex(zIndexMarqueur(isHighlighted, isSelected, f))
  }
}

function clearMarkers() {
  for (const { marker } of markers) marker.setMap(null)
  markers = []
}

watch(() => props.formateurs, () => updateMarkers(), { deep: true })

watch(() => props.highlightedId, (newId) => {
  repeindre()
  const trouve = markers.find(({ formateurId }) => formateurId === newId)
  if (map && trouve) map.panTo(trouve.marker.getPosition())
})

watch(() => props.selectedIds, () => repeindre())

watch(() => props.rayonKm, () => updateCercleRayon())
// Le contenu, pas l'identité : un tableau recréé au même contenu ne redessine rien.
watch(() => props.rayonsAvantKm.join(), () => updateCercleAvant())

watch(() => props.departementSurligne, () => appliquerStyleDepartements())

/**
 * Le point de repère se déplace, ou disparaît. QUAND IL DISPARAÎT, SON CERCLE
 * AUSSI : `updateCercleRayon` ne sait le retirer que sur un rayon nul, et sans
 * ce nettoyage un cercle orphelin restait tracé autour d'un point qu'on venait
 * d'effacer — la carte montrait alors une zone de recherche qui n'existait plus.
 */
watch(() => props.centre, () => {
  if (!map) return
  if (centreMarker) {
    centreMarker.setMap(null)
    centreMarker = null
    centreLatLng = null
  }
  if (cercleRayon) {
    cercleRayon.setMap(null)
    cercleRayon = null
  }
  placerCentre()
})
</script>

<style scoped>
.mfc {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 400px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--color-border);
}

.mfc-map {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.mfc-erreur {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-bg-secondary);
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  text-align: center;
}

.mfc-note {
  position: absolute;
  top: var(--spacing-sm);
  left: var(--spacing-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  background: var(--color-bg-primary);
  border-radius: var(--radius-sm);
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-xs);
  color: var(--color-warning-dark);
  box-shadow: var(--shadow-sm);
}

.mfc-legende {
  position: absolute;
  bottom: var(--spacing-2);
  left: var(--spacing-2);
  max-width: calc(100% - 2 * var(--spacing-2));
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-1) var(--spacing-3);
  padding: var(--spacing-1) var(--spacing-2);
  border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  color: var(--color-text-secondary);
  font-size: var(--list-font-label);
  box-shadow: var(--shadow-sm);
}

/* La légende est la MÊME des deux côtés : ses pastilles appartiennent donc au
   socle, et non recopiées dans chaque page (`:slotted`, puisqu'elles arrivent
   par le slot). */
:slotted(.legend-item) {
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  white-space: nowrap;
}

:slotted(.legend-dot) {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

:slotted(.legend-dot--lg) {
  width: 11px;
  height: 11px;
}
</style>
